import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, Star } from 'lucide-react';
import { luxuryHotels } from '../data/mock';
import { Button } from './ui/button';

export const HotelsSection = ({ onNavigate }) => {
  const { t } = useTranslation();

  return (
    <section className="py-24 bg-gradient-to-b from-stone-100 to-stone-50" id="hotels">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-serif text-stone-800 mb-4">
            {t('hotels.title')}
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            {t('hotels.subtitle')}
          </p>
        </motion.div>

        {/* Hotels Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {luxuryHotels.map((hotel, index) => (
            <motion.div
              key={hotel.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group"
            >
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg shadow-stone-900/5 hover:shadow-xl hover:shadow-stone-900/10 transition-all duration-300">
                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={hotel.image}
                    alt={hotel.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-xs font-medium text-stone-700 rounded-full">
                      {hotel.category}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4 flex items-center gap-1 px-2 py-1 bg-amber-500/90 backdrop-blur-sm rounded-full">
                    <Star className="w-3 h-3 text-white fill-white" />
                    <Star className="w-3 h-3 text-white fill-white" />
                    <Star className="w-3 h-3 text-white fill-white" />
                    <Star className="w-3 h-3 text-white fill-white" />
                    <Star className="w-3 h-3 text-white fill-white" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-serif text-stone-800">
                        {hotel.name}
                      </h3>
                      <p className="text-sm text-amber-600">
                        {hotel.destination}
                      </p>
                    </div>
                    <span className="text-sm font-medium text-stone-400">
                      {hotel.priceRange}
                    </span>
                  </div>
                  <p className="text-stone-600 text-sm leading-relaxed mb-4">
                    {hotel.description}
                  </p>
                  <Button
                    variant="ghost"
                    onClick={() => onNavigate('contact')}
                    className="p-0 h-auto text-amber-700 hover:text-amber-800 hover:bg-transparent group/btn"
                  >
                    {t('hotels.viewDetails')}
                    <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
